import React from "react";
import { Box, Typography, Link, Button, Divider } from "@mui/material";
import {
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  Email as EmailIcon,
  ArrowUpward as ArrowUpwardIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import FitbitIcon from "@mui/icons-material/Fitbit";

const Footer = () => {
  const location = useLocation();
  const [expanded, setExpanded] = React.useState(false);
  const [hovered, setHovered] = React.useState(false);

  const exploreLinks = [
    { label: "Home", href: "/" },
    { label: "Plans", href: "/plans" },
    { label: "Sign Up", href: "/signup" },
  ];

  const quickNavigate = [
    { label: "Hero", href: "#hero" },
    { label: "How It Works", href: "#howitworks" },
    { label: "Our Services", href: "#ourServices" },
    { label: "Detail Page", href: "#detail" },
  ];

  const services = [
    "Residential Installation",
    "Commercial Installations",
    "Maintenance",
    "Personalised Consultation",
  ];

  const downloadBrochure = () => {
    const link = document.createElement("a");
    link.href = "/brochure.pdf";
    link.download = "SolarConnect_Brochure.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        position: "relative",
        backgroundColor: "#e6f0fa",
        color: "#333",
        overflow: "hidden",
        transition: "max-height 0.5s ease",
        maxHeight: expanded ? 1200 : 240,
        px: 3,
      }}
    >
      {/* Overlay Hint on Hover */}
      {!expanded && hovered && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: 240,
            background: "rgba(0, 0, 0, 0.4)",
            backdropFilter: "blur(4px)",
            zIndex: 2,
            pointerEvents: "none",
          }}
        >
          <Box
            onClick={() => setExpanded(true)}
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              color: "#fff",
              pointerEvents: "auto",
              cursor: "pointer",
              textAlign: "center",
            }}
          >
            <ExpandMoreIcon sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="body2">Click to expand footer</Typography>
          </Box>
        </Box>
      )}

      {/* Top Section */}
      <Box textAlign="center" mb={6} pt={6}>
        <Typography
          variant="h5"
          fontWeight="bold"
          color={!expanded && hovered ? "#333" : "primary.main"}
          gutterBottom
          sx={{
            position: "relative",
            zIndex: 4,
            "&:hover": {
              color: "#ffaa00", // Orange hover color
              transition: "color 0.3s ease", // Smoother transition
              textShadow: "0 0 8px rgba(255, 170, 0, 0.5)", // Optional glow effect
            },
          }}
        >
          <FitbitIcon /> {" Gupta Engineers and Solar Solutions"}
        </Typography>
        <Typography
          variant="body2"
          color="#555"
          maxWidth="650px"
          mx="auto"
          mb={2}
        >
          Residential and Commercial Solar installation, Personalized consultations,
          and Solar maintenance.
        </Typography>
        <Typography
          variant="caption"
          display="block"
          mt={1}
          mb={1}
          color="#888"
        >
          © {new Date().getFullYear()} Gupta Engineers and Solar Solutions. All
          rights reserved.
        </Typography>
        <Box display="flex" justifyContent="center" gap={2}>
          {[FacebookIcon, InstagramIcon, LinkedInIcon, EmailIcon].map(
            (Icon, i) => (
              <Link
                key={i}
                href="#"
                color="inherit"
                aria-label={Icon.displayName}
                sx={{ zIndex: 3 }} // ensures above overlay if still visible
              >
                <Icon sx={{ color: "#333", "&:hover": { color: "#ffaa00" } }} />
              </Link>
            )
          )}
        </Box>
        <Divider sx={{ bgcolor: "#ccc", mt: 3 }} />
      </Box>

      {/* Mid Section */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        textAlign="center"
        gap={6}
        mb={2}
      >
        {/* Column 1 */}
        <Box flex={1}>
          <Box mb={4}>
            <Typography variant="h6" fontWeight={500} gutterBottom>
              Contact Info
            </Typography>
            <Typography variant="body2" color="#555">
              Gupta Engineers and Solar Solutions
              <br />
              Jaipur, Rajasthan
              <br />
              +91-9462824721 | +91-9116489845
              <br />
              guptayogesh484.01@gmail.com
            </Typography>
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={500} gutterBottom>
              Legal
            </Typography>
            <Link
              href="/terms"
              underline="hover"
              color="#666"
              display="block"
              mb={0.5}
            >
              Terms & Conditions
            </Link>
            <Link
              href="/privacy"
              underline="hover"
              color="#666"
              display="block"
            >
              Privacy Policy
            </Link>
          </Box>
        </Box>

        {/* Column 2 */}
        <Box
          flex={1}
          display="flex"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={4}
          textAlign={"center"}
        >
          <Box minWidth="120px">
            <Typography variant="h6" fontWeight={500} gutterBottom>
              Explore
            </Typography>
            {exploreLinks.map(({ label, href }) => (
              <Link
                key={href}
                href={href}
                underline="hover"
                color={location.pathname === href ? "primary.main" : "#666"}
                display="block"
                mb={0.5}
              >
                {label}
              </Link>
            ))}
          </Box>
          <Box minWidth="120px">
            <Typography variant="h6" fontWeight={500} gutterBottom>
              Quick Navigate
            </Typography>
            {quickNavigate.map(({ label, href }) => (
              <Link
                key={href}
                href={href}
                underline="hover"
                color={location.hash === href ? "primary.main" : "#666"}
                display="block"
                mb={0.5}
              >
                {label}
              </Link>
            ))}
          </Box>
        </Box>

        {/* Column 3 */}
        <Box flex={1} textAlign="center">
          <Box mb={4}>
            <Typography variant="h6" fontWeight={500} gutterBottom>
              Services
            </Typography>
            {services.map((service) => (
              <Typography key={service} variant="body2" color="#555" mb={0.5}>
                {service}
              </Typography>
            ))}
          </Box>
          <Button
            variant="contained"
            onClick={downloadBrochure}
            sx={{
              backgroundColor: "#ffaa00",
              color: "#000",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "6px",
              "&:hover": {
                backgroundColor: "#ff9900",
              },
            }}
          >
            Download Brochure
          </Button>
        </Box>
      </Box>

      {/* Bottom Section */}
      <Box textAlign="center" pb={3}>
        <Divider sx={{ bgcolor: "#ccc", mb: 3 }} />
        <Button
          variant="text"
          startIcon={<ArrowUpwardIcon />}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          sx={{
            color: "#ffaa00",
            textTransform: "none",
            fontWeight: 500,
            "&:hover": {
              color: "#000",
              backgroundColor: "#ffaa00",
            },
          }}
        >
          Go to Top
        </Button>
      </Box>
    </Box>
  );
};

export default Footer;
